/**
 * Reload the app onto the newest version, for real.
 *
 * A plain reload (even after registration.update()) can still be answered by
 * the old service worker from its old cache, so the page flashes and comes
 * back unchanged -- most often on desktop browsers, where the new worker
 * takes longer to download and install than we are willing to wait. So:
 * remove the service workers and clear their caches first, then reload. The
 * reload then has to come from the network, and the app registers a fresh
 * service worker (and cache) on the way back up.
 */
export async function forceRefresh(goTo?: string): Promise<void> {
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
    await Promise.all(regs.map((r) => r.unregister()));
  } catch {
    /* carry on */
  }
  try {
    if (typeof caches !== "undefined") {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch {
    /* carry on */
  }
  if (goTo) window.location.assign(goTo);
  else window.location.reload();
}

const RECOVERY_KEY = "pph-chunk-recovery-at";
const RECOVERY_COOLDOWN_MS = 30_000;

/**
 * A tab that was open across a release asks for screens (JS chunks) the new
 * release no longer has. Vite reports that as "vite:preloadError". Recover once
 * by force-refreshing -- but never in a loop: if we already tried in the last
 * 30 seconds, leave the error alone.
 */
export function installChunkErrorRecovery(): void {
  window.addEventListener("vite:preloadError", (event) => {
    let last = 0;
    try {
      last = Number(sessionStorage.getItem(RECOVERY_KEY) ?? 0);
    } catch {
      /* ignore */
    }
    if (Date.now() - last < RECOVERY_COOLDOWN_MS) return;
    try {
      sessionStorage.setItem(RECOVERY_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    event.preventDefault();
    void forceRefresh();
  });
}
